/**
 * Kalendarz (readonly, PLAN §8): agenda na najbliższe 7 dni, grupowana po dniu.
 *
 * Czyta wyłącznie cache backendu (`/api/calendar/events`) - żadnego ruchu do
 * Google z przeglądarki. Wydarzenie wielodniowe pojawia się w każdym dniu,
 * który obejmuje: to jest twardy krajobraz, a nie lista rekordów.
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../api";
import type { CalendarEvent } from "../settings/types";
import { useSpaceParam } from "./hooks";

/** Ile dni pokazuje agenda (dziś + 6). */
const AGENDA_DAYS = 7;

const DAY_FORMAT = new Intl.DateTimeFormat("pl-PL", {
  weekday: "long",
  day: "numeric",
  month: "long",
});

function isoDate(offsetDays: number): string {
  const day = new Date();
  day.setHours(12, 0, 0, 0);
  day.setDate(day.getDate() + offsetDays);
  return day.toISOString().slice(0, 10);
}

function dayLabel(iso: string): string {
  const today = isoDate(0);
  if (iso === today) return "dziś";
  if (iso === isoDate(1)) return "jutro";
  return DAY_FORMAT.format(new Date(`${iso}T12:00:00`));
}

export function CalendarView() {
  const space = useSpaceParam();
  const from = isoDate(0);
  const to = isoDate(AGENDA_DAYS - 1);

  const { data, isLoading, error } = useQuery({
    queryKey: ["calendar-events", from, to, space ?? "all"],
    queryFn: () => api.calendarEvents({ date_from: from, date_to: to, space }),
  });

  const days = Array.from({ length: AGENDA_DAYS }, (_, i) => isoDate(i));

  if (isLoading) return <div className="view"><p className="muted">Ładowanie…</p></div>;
  if (error) {
    return (
      <div className="view">
        <p className="error">Nie udało się pobrać kalendarza.</p>
      </div>
    );
  }

  if (!data?.connected) {
    return (
      <div className="view">
        <p className="muted">
          Kalendarz Google nie jest połączony. Zrób to w <Link to="/ustawienia">Ustawieniach</Link>.
        </p>
      </div>
    );
  }

  if (data.items.length === 0) {
    return (
      <div className="view">
        <p className="muted">Nic w kalendarzu przez najbliższe {AGENDA_DAYS} dni.</p>
      </div>
    );
  }

  return (
    <div className="view">
      {days.map((day) => {
        // Wydarzenie „zajmuje” dzień, jeśli mieści się on w [date, end_date].
        const events = data.items.filter((e) => e.date <= day && e.end_date >= day);
        if (events.length === 0) return null;
        return (
          <section className="group" key={day}>
            <h3 className="group__title">
              {dayLabel(day)} <span className="group__count">({events.length})</span>
            </h3>
            <ul className="rows">
              {events.map((event) => (
                <EventRow key={`${event.id}-${day}`} event={event} />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function EventRow({ event }: { event: CalendarEvent }) {
  const when = event.all_day
    ? "cały dzień"
    : `${event.start ?? ""}${event.end ? `–${event.end}` : ""}`;

  return (
    <li className="row">
      <div className="row__main">
        <span className="row__title">{event.summary}</span>
        <span className="row__meta">
          <strong>{when}</strong>
          {event.location && <span>{event.location}</span>}
          {event.space && <span className="badge">{event.space}</span>}
        </span>
      </div>
    </li>
  );
}
